/* RuntimeConfigProvider — exposes the boot-time runtime config to feature code.
 *
 * The donation export endpoint streams CSV (non-envelope), so a few features
 * need direct access to `apiBaseUrl` outside the ApiClient. Rather than
 * importing the raw config at module scope (and making components implicitly
 * coupled to global state), this provider tunnels it through React context.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';

const RuntimeConfigContext = createContext<RuntimeConfig | null>(null);

interface RuntimeConfigProviderProps {
  config: RuntimeConfig;
  children: ReactNode;
}

export function RuntimeConfigProvider({
  config,
  children,
}: RuntimeConfigProviderProps): JSX.Element {
  const value = useMemo(() => config, [config]);
  return (
    <RuntimeConfigContext.Provider value={value}>
      {children}
    </RuntimeConfigContext.Provider>
  );
}

export function useRuntimeConfig(): RuntimeConfig {
  const ctx = useContext(RuntimeConfigContext);
  if (ctx === null) {
    throw new Error(
      'useRuntimeConfig must be used inside <RuntimeConfigProvider>'
    );
  }
  return ctx;
}
